import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { MapControllerInput } from '@app/components/openstreetmap/interfaces/controller-input.interface';
import { Loading } from '@app/components/ui/loading/loading';
import { ExternalSceneQuickViewDialogComponent } from '../external-scene-quick-view-dialog/external-scene-quick-view-dialog.component';
import { ExternalScenesStateService } from '../../external-scenes/external-scenes-state.service';
import { ExternalSceneProvider, ExternalSceneRowViewModel } from '../../external-scenes/external-scenes.models';
import { ExternalScenesTableComponent } from '../external-scenes-table/external-scenes-table.component';
import { buildFootprintPolygons } from '../../external-scenes/external-scenes-map.util';

const TOAST_LIFE_MS = 4000;

const PROVIDER_NAMES: Record<ExternalSceneProvider, string> = {
  Sentinel2: 'Sentinel-2',
  Landsat: 'Landsat'
};

/**
 * Content of the `download` tab: counter, hint box, warnings, loading / empty / initial states and the result
 * table. It renders the store provided by the page and mirrors the scene footprints on the page's map, removing
 * them again when the tab is left. The filters live in the page's filter panel and call `search` on the store.
 */
@Component({
  selector: 'app-external-scenes-view',
  standalone: true,
  templateUrl: './external-scenes-view.component.html',
  styleUrl: './external-scenes-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, Loading, ExternalScenesTableComponent, ExternalSceneQuickViewDialogComponent]
})
export class ExternalScenesViewComponent {
  /** Controller of the page's map; footprints are drawn and removed through it. */
  readonly mapController = input.required<Subject<MapControllerInput>>();

  private readonly store = inject(ExternalScenesStateService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly rows = this.store.rows;
  readonly counters = this.store.counters;
  readonly isSearching = this.store.isSearching;
  readonly hasSearched = this.store.hasSearched;
  readonly isEmpty = this.store.isEmpty;
  readonly errorKey = this.store.errorKey;

  readonly truncatedProviderNames = computed(() =>
    this.store
      .truncatedProviders()
      .map(provider => PROVIDER_NAMES[provider])
      .join(', ')
  );

  readonly providerWarnings = computed(() =>
    this.store.providerErrors().map(error => ({ provider: PROVIDER_NAMES[error.provider], message: error.message }))
  );

  private readonly selectedRowId = signal<string | null>(null);
  readonly selectedRow = computed(() => this.rows().find(row => row.id === this.selectedRowId()) ?? null);

  /** Ids of the footprint polygons currently on the map. */
  private drawnPolygonIds: string[] = [];

  constructor() {
    this.store.loadCreated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('EXTERNAL_SCENES.LOAD_CREATED_TOAST'),
          life: TOAST_LIFE_MS
        })
      );

    effect(() => this.drawFootprints(this.rows()));
    this.destroyRef.onDestroy(() => this.removeFootprints());
  }

  onPreviewClick(rowId: string): void {
    this.selectedRowId.set(rowId);
  }

  onDialogClosed(): void {
    this.selectedRowId.set(null);
  }

  onLoadClick(rowId: string): void {
    this.store.load(rowId);
  }

  /** Replaces the footprints on the map with those of the current rows. */
  private drawFootprints(rows: readonly ExternalSceneRowViewModel[]): void {
    const polygons = buildFootprintPolygons(rows);
    this.mapController().next({ removePolygons: this.drawnPolygonIds, addGisPolygons: polygons });
    this.drawnPolygonIds = polygons.map(polygon => polygon.id);
  }

  private removeFootprints(): void {
    if (this.drawnPolygonIds.length > 0) {
      this.mapController().next({ removePolygons: this.drawnPolygonIds });
      this.drawnPolygonIds = [];
    }
  }
}
